import React, { useEffect, useRef, useState } from "react";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import { SunIcon, MoonIcon } from "../components/Icons";
import useImageBrightness from "../useImageBrightness";
import useTheme from "../useTheme";
import { useLang } from "../LangContext";

interface Props {
  workerFirstName?: string | null;
  workerCustomPublicId?: string | null;
  workerLogoUrl?: string | null;
  onOpenSettings: () => void;
  onOpenAbout: () => void;
  onLogout: () => void;
}

export default function DashboardTopbar({
  workerFirstName,
  workerCustomPublicId,
  workerLogoUrl,
  onOpenSettings,
  onOpenAbout,
  onLogout,
}: Props) {
  const { t } = useLang();
  const [theme, toggleTheme] = useTheme();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const activeLogoUrl = workerLogoUrl && !logoError ? workerLogoUrl : null;
  const imageTone = useImageBrightness(activeLogoUrl);
  const avatarBg: string | undefined = !activeLogoUrl ? undefined
    : imageTone === 'light' ? '#1e293b'
    : imageTone === 'dark'  ? '#f1f5f9'
    : '#64748b';

  useEffect(() => {
    if (!showProfileMenu) return;
    const onDocClick = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showProfileMenu]);

  return (
    <div className="dashboard-topbar">
      <nav className="dashboard-nav">
        <span className="dashboard-brand" aria-hidden="true">
          <Logo size={28} />
        </span>
      </nav>
      <span className="dashboard-welcome">{workerCustomPublicId || workerFirstName || ''}</span>
      <div className="topbar-actions">
        <LangToggle />
        <button
          type="button"
          className="theme-toggle"
          aria-label={theme === "dark" ? t.switchToLight : t.switchToDark}
          onClick={toggleTheme}
        >
          {theme === "dark" ? <SunIcon /> : <MoonIcon />}
        </button>
        <div className="profile-menu-wrap" ref={profileMenuRef}>
          <button
            type="button"
            className="profile-avatar"
            aria-label={t.openProfileMenu}
            aria-expanded={showProfileMenu}
            onClick={() => setShowProfileMenu((v) => !v)}
            style={avatarBg ? { background: avatarBg } : undefined}
          >
            {workerLogoUrl && !logoError ? (
              <img
                src={workerLogoUrl}
                alt=""
                aria-hidden="true"
                className="profile-avatar-img"
                onError={() => setLogoError(true)}
              />
            ) : (
              <span className="profile-avatar-glyph" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                  <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6Z" />
                </svg>
              </span>
            )}
          </button>
          {showProfileMenu && (
            <div className="profile-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="profile-menu-item"
                onClick={() => {
                  setShowProfileMenu(false);
                  onOpenSettings();
                }}
              >
                {t.menuSettings}
              </button>
              <button
                type="button"
                role="menuitem"
                className="profile-menu-item"
                onClick={() => {
                  setShowProfileMenu(false);
                  onOpenAbout();
                }}
              >
                {t.menuAbout}
              </button>
              <button
                type="button"
                role="menuitem"
                className="profile-menu-item"
                onClick={() => {
                  setShowProfileMenu(false);
                  onLogout();
                }}
              >
                {t.menuSignOut}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
